import React, {useContext, useState} from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import {Link} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowRight01Icon, ArrowLeft01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {OpenbrainLogoLink} from "../../../components/links/OpenbrainLogoLink";
import {FitrLogoLink} from "../../../components/links/FitrLogoLink";
import {c, mono, softShadow, primaryButtonSx} from "../../landing/landingTokens";
import {LiquidEther} from "../../../components/voice-orb";

// Soft, borderless filled fields — rounded, faint fill, no underline. On focus the fill
// warms to a light purple and a gentle glow ring replaces any hard border.
const fieldSx = {
  "& .MuiFilledInput-root": {
    borderRadius: "12px",
    backgroundColor: "rgba(20,21,26,0.04)",
    overflow: "hidden",
    transition: "background-color .2s ease, box-shadow .2s ease",
    "&:before, &:after": {display: "none"},
    "&:hover": {backgroundColor: "rgba(20,21,26,0.06)"},
    "&.Mui-focused": {
      backgroundColor: "rgba(129,140,248,0.08)",
      boxShadow: "0 0 0 2px rgba(129,140,248,0.45)",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {color: c.purpleDeep},
};

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    lastname: "",
    email: "",
    password: "",
    phone_number: "",
    company_name: "",
  })
  const {onRegister} = useContext(SignInContext);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const onChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;

    updateForm(key, value);
    setErrors(prev => ({...prev, [key]: ""}));
  }

  const updateForm = (key, value) => {
    setFormData(prev => ({...prev, [key]: value}));
  }

  const registerHandle = async (e) => {
    e.preventDefault();

    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData?.name?.trim()) nextErrors.name = "Name is required";
    if (!formData?.lastname?.trim()) nextErrors.lastname = "Last name is required";
    if (!formData?.email?.trim()) {
      nextErrors.email = "Email is required";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address";
    }
    if (!formData?.phone_number?.trim()) {
      nextErrors.phone_number = "Phone number is required";
    } else if (!/^\d+$/.test(formData.phone_number.trim())) {
      nextErrors.phone_number = "Phone number can contain numbers only";
    }
    if (!formData?.password) {
      nextErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      nextErrors.password = "Password must be at least 8 characters long";
    } else if (!/[0-9]/.test(formData.password)) {
      nextErrors.password = "Password must contain at least one number";
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      nextErrors.password = "Password must contain at least one special character";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    try {
      setLoading(true);
      await onRegister({...formData, username: formData?.email});
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{
      position: "relative", width: "100%", minHeight: "100%",
      bgcolor: c.bg, display: "flex", flexDirection: "column", overflow: "hidden"
    }}>
      {/* Mouse-reactive fluid backdrop (React Bits LiquidEther) — matches the landing hero,
          toned down for an auth page. autoDemo is disabled when the user prefers reduced motion. */}
      <Box aria-hidden sx={{position: "absolute", inset: 0, zIndex: 0, opacity: 0.3, pointerEvents: "none"}}>
        <LiquidEther
          colors={["#8B7CFF", "#E2A6FF", "#C7D2FE"]}
          mouseForce={12}
          cursorSize={90}
          resolution={0.5}
          autoDemo={!reduceMotion}
          autoSpeed={0.3}
          autoIntensity={1.0}
          autoResumeDelay={800}
          autoRampDuration={1}
        />
      </Box>

      <Stack sx={{position: "relative", zIndex: 1, flex: 1, minHeight: "100%"}}>
        {/* TOP BAR — logo + back-to-marketing link */}
        <Toolbar sx={{gap: 1.75}}>
          <img src={"/logo.svg"} alt={"logo"} width={30}/>
          <Box
              component={Link}
              to="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontFamily: mono,
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: c.purpleDeep,
                textDecoration: "none",
                opacity: 0.9,
                "&:hover": {opacity: 1},
              }}
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={10}/> openbrain.io
          </Box>
        </Toolbar>

        {/* CARD */}
        <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 2, py: 3}}>
          <Stack
            direction={"column"} gap={3}
            sx={{
              maxWidth: 440, width: "100%", boxSizing: "border-box",
              px: {xs: 3, sm: 4.5}, py: {xs: 4, sm: 5},
              borderRadius: "22px", bgcolor: c.white, boxShadow: softShadow, mx: "auto"
            }}
            justifyContent={'center'} alignItems={"center"}
          >
            {/*HEADER*/}
            <Stack direction={"column"} gap={1.25} sx={{width: '100%'}}>
              <Typography sx={{
                fontFamily: mono, fontSize: 11.5, letterSpacing: "0.16em",
                textTransform: "uppercase", color: c.purpleDeep, textAlign: "center"
              }}>
                OpenBrain
              </Typography>
              {/*TITLE*/}
              <Typography sx={{
                fontSize: {xs: 34, sm: 42}, fontWeight: 700, letterSpacing: "-0.03em",
                color: c.ink, textAlign: "center", lineHeight: 1.05
              }}>
                Sign up
              </Typography>
              {/*SIGN IN*/}
              <Box sx={{textAlign: "center"}}>
                <Typography sx={{color: c.inkSoft, fontSize: 14.5}} component={"span"}>
                  Already have an account?
                </Typography>
                <Link to={`/login${window.location.search}`} style={{
                  paddingLeft: 5,
                  color: c.purpleDeep,
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer"
                }}>
                  Sign in
                </Link>
              </Box>
            </Stack>
            {/*REGISTER FORM*/}
            <Stack component={"form"} onSubmit={registerHandle} direction={"column"} gap={1.5} sx={{width: '100%'}}>
              <Stack direction={"row"} gap={1.5}>
                <TextField
                  variant={"filled"}
                  required
                  fullWidth
                  label={"Name"}
                  value={formData?.name}
                  name={"name"}
                  onChange={onChange}
                  placeholder={"Enter your first name"}
                  sx={fieldSx}
                />
                <TextField
                  variant={"filled"}
                  required
                  fullWidth
                  label={"Last name"}
                  value={formData?.lastname}
                  name={"lastname"}
                  onChange={onChange}
                  placeholder={"Enter your last name"}
                  sx={fieldSx}
                />
              </Stack>
              <TextField
                variant={"filled"}
                required
                fullWidth
                label={"Email"}
                type={"email"}
                value={formData?.email}
                name={"email"}
                onChange={onChange}
                placeholder={"Enter your email address"}
                error={!!errors.email}
                helperText={errors.email}
                sx={fieldSx}
              />
              <TextField
                variant={"filled"}
                required
                fullWidth
                label={"Phone"}
                type={"tel"}
                inputProps={{inputMode: "numeric"}}
                value={formData?.phone_number}
                name={"phone_number"}
                onChange={onChange}
                placeholder={"Enter your phone number"}
                error={!!errors.phone_number}
                helperText={errors.phone_number}
                sx={fieldSx}
              />
              <TextField
                variant={"filled"}
                fullWidth
                label={"Company name"}
                value={formData?.company_name}
                name={"company_name"}
                onChange={onChange}
                placeholder={"Enter your company name"}
                sx={fieldSx}
              />
              <TextField
                autoFocus
                type={"password"}
                variant={"filled"}
                required
                fullWidth
                name={"password"}
                label={"Password"}
                value={formData?.password}
                placeholder={"Enter your password"}
                onChange={onChange}
                error={!!errors.password}
                helperText={errors.password}
                sx={fieldSx}
              />

              <Stack direction={"row"} gap={2} justifyContent={"space-between"} alignItems={"center"}
                     useFlexGap flexWrap={"wrap"}>
                <FormControlLabel sx={{color: c.inkSoft}} control={<Checkbox defaultChecked
                                                                             sx={{"&.Mui-checked": {color: c.purpleDeep}}}/>}
                                  label="Remember me"/>
                <Typography sx={{textDecoration: "none", color: c.inkSoft, fontSize: 14}} component={Link}
                            to={"/forgot-password"}>Forgot password?</Typography>
              </Stack>
              <Button
                fullWidth
                type={"submit"}
                disabled={loading || !formData?.name || !formData?.lastname || !formData?.email || !formData?.password}
                sx={{...primaryButtonSx, py: 1.4, fontSize: 15}}
                endIcon={loading
                  ? <CircularProgress size={16} color={"inherit"}/>
                  : <HugeiconsIcon icon={ArrowRight01Icon} size={13}/>}
              >
                Continue
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* FOOTER */}
        <Toolbar sx={{justifyContent: "space-between"}}>
          <OpenbrainLogoLink/>
          <FitrLogoLink/>
        </Toolbar>
      </Stack>
    </Box>
  )
}
